import { defineComponent as _defineComponent } from 'vue'
import BCXIcon from '@/components/molecules/BCXIcon.vue';
import useToastUiEditorTools from '@/mixins/useToastUiEditorTools';
import {
  Chat,
  CHAT_TYPE_DIRECT,
  CHAT_TYPE_FORUM,
  CHAT_TYPE_GROUP,
  CHAT_TYPE_PROJECT,
  ChatMessageType,
  MESSAGE_EVENT_SET_TEXT,
  MessengerMaxChars,
  SendMessageResult
} from '@/models/Messenger';
import useMessengerState from '@/state/messenger/messengerState';
import stripTags from '@/utils/stripTags';
import { Editor } from '@toast-ui/vue-editor';
import ToastEditor from '@toast-ui/editor';
import {
  computed, onBeforeUnmount, Ref, ref, toRefs, watch
} from 'vue';
import { onClickOutside, onKeyDown } from '@vueuse/core';
import removeTrailingEmptyLinesFromHTML from '@/utils/removeTrailingEmptyLinesFromHTML';
import eventBus from '@/state/messenger/EventBus';


export default /*#__PURE__*/_defineComponent({
  __name: 'BCXMessengerChatInput',
  props: {
  chat: {
    type: Object as () => Chat,
    default: () => null,
  },
},
  setup(__props) {

const props = __props;



const { chat } = toRefs(props);
const {
  sendMessage, editMessage,
  isInputFocussed, selectedChatText, setSelectedChatText,
  isInputMaximized, editMessageId, getMessageById,
  isWaitingForData, preventInputUnFocusOnce,
} = useMessengerState();
const isToolbarVisible = ref(false);
const editor = ref<ToastEditor>();
const chatInput = ref(null);
const lastSendMessageResult = ref(SendMessageResult.OK);

// const input = templateRef('input');

// onClickOutside(input, () => { isInputFocussed.value = false; });

const editedText = ref('');

const text = computed({
  get: () => (editMessageId.value ? editedText.value : selectedChatText.value),
  set: (text) => {
    if (editMessageId.value) editedText.value = text;
    else setSelectedChatText(text);
  },
});

watch(chat, () => {
  lastSendMessageResult.value = SendMessageResult.OK;
});

watch(editMessageId, (id) => {
  editedText.value = getMessageById(id)?.text ?? ''; // clear edited text on edit select:
  if (!editor.value) return;
  editor.value.setMarkdown(editedText.value, !!id);
});

const getMessageType = () => {
  switch (chat.value?.type) {
    case 'PRIVATE_MESSAGING': return ChatMessageType.PRIVATE_MESSAGING_MESSAGE;
    case 'PROJECT_INTERNAL_MESSAGING': return ChatMessageType.PROJECT_THREAD_MESSAGE;
    case 'FORUM_INTERNAL_MESSAGING': return ChatMessageType.FORUM_THREAD_MESSAGE;
    default: return ChatMessageType.PRIVATE_FEEDBACK_MESSAGING;
  }
};

const send = async () => {
  const strippedText = stripTags(text.value);
  if (strippedText.trim()) {
    const textToSend = removeTrailingEmptyLinesFromHTML(text.value);
    if (editMessageId.value) {
      lastSendMessageResult.value = await editMessage(textToSend);
      editMessageId.value = '';
    } else {
      // explicitly don't wait for it to be finished.
      sendMessage(textToSend, getMessageType()).then((result) => {
        lastSendMessageResult.value = result;
      });
      text.value = '';
    }
    if (!editor.value) return;
    editor.value.setMarkdown('');
  }
};

onKeyDown('Enter', (evt) => {
  if (evt.ctrlKey) {
    send();
    evt.preventDefault();
  }
}, {
  target: chatInput as unknown as Ref<EventTarget>
});

const focus = (state:boolean) => {
  if (preventInputUnFocusOnce.value) {
    preventInputUnFocusOnce.value = false;
    return;
  }
  isInputFocussed.value = state;
};

onClickOutside(chatInput, () => focus(false));

onBeforeUnmount(() => {
  isInputFocussed.value = false;
});

const isPrivate = computed(() => chat.value.type === CHAT_TYPE_DIRECT);
const isGroup = computed(() => chat.value.type === CHAT_TYPE_GROUP);

const showAsFocussed = computed(() => isInputFocussed.value && !isInputMaximized.value);

const maxLength = computed(() => {
  switch (chat.value.type) {
    case CHAT_TYPE_DIRECT: return MessengerMaxChars.CHAT_TYPE_DIRECT;
    case CHAT_TYPE_FORUM: return MessengerMaxChars.CHAT_TYPE_FORUM;
    case CHAT_TYPE_PROJECT: return MessengerMaxChars.CHAT_TYPE_PROJECT;
    case CHAT_TYPE_GROUP: return MessengerMaxChars.CHAT_TYPE_GROUP;
    default:
  }
  return 0;
});

const { createButton } = useToastUiEditorTools(editor, maxLength);

let lastMarkdown = '';
const onEditorInput = () => {
  if (!editor.value) return;
  const markdown = editor.value.getMarkdown();
  if (markdown === lastMarkdown) return;
  lastMarkdown = markdown;
  text.value = editor.value.getHTML();
};

const addImageBlobHook = (blob:any, callback:any) => {
  callback('', '');
};

const classes = computed(() => ({
  'c-messenger-chat-input--maximized': isInputMaximized.value,
  'c-messenger-chat-input--focussed': showAsFocussed.value,
  'c-messenger-chat-input--edited': !!editMessageId.value,
  'c-messenger-chat-input--waiting': isWaitingForData.value,
}));

const toggleToolbar = () => {
  if (!editor.value) return;
  isToolbarVisible.value = !isToolbarVisible.value;
  editor.value.focus();
};

eventBus.on((evt, payload) => {
  if (evt === MESSAGE_EVENT_SET_TEXT) {
    text.value = payload;
    if (!editor.value) return;
    editor.value.setMarkdown(payload);
  }
});

const editorOptions = {
  usageStatistics: false,
  hideModeSwitch: true,
  autofocus: false,
  minHeight: '100%',
  hooks: {
    addImageBlobHook
  },
  toolbarItems: [
    ['bold', 'italic', 'quote', 'ul', 'ol', 'indent', 'outdent', 'link',
      {
        el: createButton('undo'),
        command: 'undo',
        tooltip: 'Undo'
      }, {
        el: createButton('redo'),
        command: 'redo',
        tooltip: 'Redo'
      }]
  ]
};


return { __sfc: true,props, chat, sendMessage, editMessage, isInputFocussed, selectedChatText, setSelectedChatText, isInputMaximized, editMessageId, getMessageById, isWaitingForData, preventInputUnFocusOnce, isToolbarVisible, editor, chatInput, lastSendMessageResult, editedText, text, getMessageType, send, focus, isPrivate, isGroup, showAsFocussed, maxLength, createButton, lastMarkdown, onEditorInput, addImageBlobHook, classes, toggleToolbar, editorOptions, BCXIcon, SendMessageResult, Editor }
}

})